import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

const PERSONA_LABELS: Record<string, string> = {
  executive: 'Executive',
  operational: 'Operations',
  financial: 'Finance',
  vendor: 'Vendor',
  painter: 'Painter',
  compliance: 'Compliance',
  sla: 'SLA',
  sales: 'Sales',
  branding: 'Branding',
  management: 'Management',
};

function renderValue(value: unknown): ReactNode {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return `${value.length} item(s)`;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

const DRILL_LEVELS = ['REGION', 'TERRITORY', 'VENDOR', 'JOB', 'TRANSACTION'] as const;

/** L16.04 — Persona dashboard (`/dashboards/persona/:persona`). Calls the
 *  dedicated persona endpoint (`GET /v1/m16/dashboards/:persona`) rather than the
 *  generic config viewer, surfacing each persona's rich metrics (M5), and offers
 *  hierarchical drill-down (`GET /dashboards/drill-down/:level`). */
export function M16PersonaDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { persona } = useParams<{ persona: string }>();
  const { token } = useAuth();
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drillLevel, setDrillLevel] = useState<string>('REGION');
  const [drillRows, setDrillRows] = useState<Array<Record<string, unknown>>>([]);
  const [drillBusy, setDrillBusy] = useState(false);
  const [drillError, setDrillError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<Record<string, unknown>>(`/v1/m16/dashboards/${persona}`, { token })
      .then((r) => { if (alive) setData(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [persona, token]);

  async function drill(): Promise<void> {
    setDrillBusy(true);
    setDrillError(null);
    try {
      const r = await apiRequest<Array<Record<string, unknown>>>(
        `/v1/m16/dashboards/drill-down/${drillLevel}`,
        { token },
      );
      setDrillRows(r);
    } catch (e) {
      setDrillError((e as Error).message);
    } finally {
      setDrillBusy(false);
    }
  }

  if (error) return <p role="alert">{error}</p>;
  if (!data) return <p>{t('m16.persona.loading')}</p>;

  const label = PERSONA_LABELS[persona ?? ''] ?? (persona ?? 'Dashboard');
  const entries = Object.entries(data).filter(([k]) => k !== 'dashboard');

  return (
    <main aria-label="persona-dashboard">
      <h1>{t('m16.persona.dashboardTitle', { label })}</h1>
      <dl aria-label="persona-metrics">
        {entries.map(([k, v]) => (
          <div key={k} data-testid={`metric-${k}`}>
            <dt>{k}</dt>
            <dd>{renderValue(v)}</dd>
          </div>
        ))}
      </dl>

      <section aria-label="drill-down">
        <h2>{t('m16.persona.drillDown')}</h2>
        <select aria-label="drill-level" data-testid="drill-level"
          value={drillLevel} onChange={(e) => setDrillLevel(e.target.value)}>
          {DRILL_LEVELS.map((lv) => <option key={lv} value={lv}>{lv}</option>)}
        </select>
        <button type="button" data-testid="drill-btn" disabled={drillBusy} onClick={drill}>{t('m16.persona.drillBtn')}</button>
        {drillError && <p role="alert">{drillError}</p>}
        {drillRows.length > 0 && (
          <ul aria-label="drill-results">
            {drillRows.map((row, i) => (
              <li key={i} data-testid={`drill-row-${i}`}>{JSON.stringify(row)}</li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
